import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'

const TimerContext = createContext(null)

let nextId = 1

export function TimerProvider({ children }) {
  const [timers, setTimers] = useState([])
  const intervalRef = useRef(null)

  // Tick all running timers every second
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setTimers(prev => prev.map(t => {
        if (!t.running || t.done) return t
        const remaining = t.remaining - 1
        if (remaining <= 0) {
          // Play alert sound
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)()
            const osc = ctx.createOscillator()
            const gain = ctx.createGain()
            osc.connect(gain); gain.connect(ctx.destination)
            osc.frequency.value = 880
            osc.type = 'sine'
            gain.gain.setValueAtTime(0.4, ctx.currentTime)
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.5)
            osc.start(ctx.currentTime)
            osc.stop(ctx.currentTime + 1.5)
          } catch { /* audio not supported */ }
          return { ...t, remaining: 0, running: false, done: true }
        }
        return { ...t, remaining }
      }))
    }, 1000)
    return () => clearInterval(intervalRef.current)
  }, [])

  const addTimer = useCallback((label, durationSeconds, recipeId, recipeTitle) => {
    const id = nextId++
    setTimers(prev => [...prev, {
      id, label, total: durationSeconds, remaining: durationSeconds,
      running: true, done: false, recipeId, recipeTitle,
    }])
    return id
  }, [])

  const addCustomTimer = useCallback((label, durationSeconds) => {
    return addTimer(label, durationSeconds, null, 'Custom')
  }, [addTimer])

  const toggleTimer = useCallback((id) => {
    setTimers(prev => prev.map(t =>
      t.id === id ? { ...t, running: !t.running, done: false } : t
    ))
  }, [])

  const resetTimer = useCallback((id) => {
    setTimers(prev => prev.map(t =>
      t.id === id ? { ...t, remaining: t.total, running: false, done: false } : t
    ))
  }, [])

  const removeTimer = useCallback((id) => {
    setTimers(prev => prev.filter(t => t.id !== id))
  }, [])

  const getRecipeTimers = useCallback((recipeId) => {
    return timers.filter(t => t.recipeId === recipeId)
  }, [timers])

  const activeCount = timers.filter(t => t.running || t.done).length

  return (
    <TimerContext.Provider value={{
      timers, addTimer, addCustomTimer, toggleTimer, resetTimer, removeTimer,
      getRecipeTimers, activeCount,
    }}>
      {children}
    </TimerContext.Provider>
  )
}

export function useTimers() {
  const ctx = useContext(TimerContext)
  if (!ctx) throw new Error('useTimers must be inside TimerProvider')
  return ctx
}

export function formatTime(seconds) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
}
